<template>
  <div class="review-deck">
    <!-- Progress. The whole point of the deck is that it ENDS, so the count
         is not decoration — it's the reason this beats an open-ended list. -->
    <div class="review-progress">
      <div class="flex items-center justify-between mb-2">
        <span class="label-caps">{{ $t('review.progress', { done: index, total: cards.length }) }}</span>
        <span v-if="remaining > 0" style="font-size: var(--text-xs); color: var(--text-tertiary);">
          {{ $t('review.remaining', { count: remaining }) }}
        </span>
      </div>
      <AppProgress :value="cards.length ? (index / cards.length) * 100 : 100" />
    </div>

    <!-- The card. `touch-action: pan-y` keeps vertical scrolling working while
         the horizontal drag is ours. -->
    <div
      v-if="current"
      ref="cardEl"
      class="review-card"
      :class="{ 'review-card--dragging': isDragging }"
      :style="cardStyle"
      role="group"
      :aria-label="$t('review.card_label', { description: current.expense.description })"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerEnd"
      @pointercancel="onPointerEnd"
    >
      <!-- Swipe affordances: they fade in with the finger so a half-drag reads
           as a control rather than a broken card. -->
      <span class="review-hint review-hint--skip" :style="{ opacity: hintOpacity.skip }" aria-hidden="true">
        {{ $t('review.skip') }}
      </span>
      <span
        v-if="primary"
        class="review-hint review-hint--accept"
        :style="{ opacity: hintOpacity.accept }"
        aria-hidden="true"
      >
        {{ primary.label }}
      </span>

      <AppBadge :variant="reasonVariant">{{ $t(`review.reason.${current.reason}`) }}</AppBadge>

      <p class="review-amount">{{ formatMoney(current.expense.amount) }}</p>
      <p class="review-description">{{ current.expense.description }}</p>
      <p class="review-meta">
        {{ formatDate(current.expense.date) }}
        <template v-if="accountLabel"> &middot; {{ accountLabel }}</template>
      </p>

      <p class="review-question">{{ $t(`review.question.${current.reason}`) }}</p>

      <!-- Duplicate: the two answers are symmetric, so both get a button. -->
      <div v-if="current.reason === 'suspected_duplicate'" class="review-actions">
        <AppButton :disabled="busy" @click="apply({ reviewed: true }, 'review.toast.duplicate_kept')">
          {{ $t('review.action.confirm_duplicate') }}
        </AppButton>
        <AppButton
          variant="outline"
          :disabled="busy"
          @click="apply({ duplicateOf: null, reviewed: true }, 'review.toast.duplicate_cleared')"
        >
          {{ $t('review.action.keep_both') }}
        </AppButton>
      </div>

      <!-- Transfer: never pre-applied. The bank's wording is a hint, not a
           fact — on an envelope-account setup the "transfer" IS the purchase. -->
      <div v-else-if="current.reason === 'possible_transfer'" class="review-actions">
        <AppButton :disabled="busy" @click="apply({ isTransfer: true, reviewed: true }, 'review.toast.transfer_marked')">
          {{ $t('review.action.confirm_transfer') }}
        </AppButton>
        <AppButton variant="outline" :disabled="busy" @click="apply({ reviewed: true }, 'review.toast.reviewed')">
          {{ $t('review.action.not_transfer') }}
        </AppButton>
      </div>

      <!-- Categorization. The suggested chip is the one-tap path and the same
           choice swipe-right takes; the select is the long way round. -->
      <div v-else class="review-categorize">
        <AppChip
          v-if="suggestedCategory"
          selected
          :color="suggestedCategory.color"
          :button-label="$t('review.apply_suggestion', { category: suggestedCategory.name })"
          @click="apply({ category: suggestedCategory.id, reviewed: true }, 'review.toast.categorized')"
        >
          {{ suggestedCategory.name }}
        </AppChip>
        <SearchableSelect
          v-model="pickedCategory"
          :options="categoryOptions"
          :placeholder="$t('review.pick_category')"
          :search-placeholder="$t('common.search_placeholder')"
          :empty-text="$t('common.no_results')"
          :aria-label="$t('review.pick_category')"
        />
      </div>

      <div class="review-secondary">
        <AppButton variant="ghost" :disabled="busy" @click="skip">
          {{ $t('review.skip') }}
        </AppButton>
        <AppButton
          v-if="current.reason === 'uncategorized_bank' || current.reason === 'uncategorized_manual'"
          variant="link"
          :disabled="busy"
          @click="apply({ reviewed: true }, 'review.toast.reviewed')"
        >
          {{ $t('review.action.leave_uncategorized') }}
        </AppButton>
      </div>
    </div>

    <!-- Done. The completion state IS the feature — a deck that silently
         redirects back to the dashboard teaches nothing. -->
    <EmptyState
      v-else
      :title="$t('review.done_title')"
      :message="handled > 0 ? $t('review.done_message', { count: handled }) : $t('review.done_nothing')"
    >
      <AppButton @click="$emit('finish')">{{ $t('review.done_close') }}</AppButton>
    </EmptyState>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { formatMoney } from "../../composables/useCurrency"
import { useFormatDate } from "../../composables/useFormatDate"
import { hapticLight, hapticMedium } from "../../services/hapticsService"
import type { FirestoreAccount, FirestoreCategory, FirestoreExpense } from "../../types/firestore"
import type { ReviewCard } from "../../utils/reviewQueue"
import { suggestCategoryForMerchant } from "../../utils/reviewQueue"
import { AppBadge, AppButton, AppChip, AppProgress, EmptyState, SearchableSelect } from "../ui"

/**
 * The card deck for the weekly review.
 *
 * Presentational: it decides what to ask and emits the answer. The parent owns
 * every write, so a failed mutation is handled in one place instead of four.
 *
 * `cards` is a SNAPSHOT taken when the sheet opened, not a live list. A live
 * list would renumber and reorder itself under the user's finger every time a
 * mutation landed — and in a household, every time the other member's did too.
 */
interface Props {
  cards: ReviewCard[]
  categories: FirestoreCategory[]
  accounts: FirestoreAccount[]
  /** merchant key → category id, from buildMerchantSuggestionIndex. */
  suggestionIndex: Map<string, string>
  /** A write is in flight — controls lock, but the deck stays readable. */
  busy?: boolean
}

const props = withDefaults(defineProps<Props>(), { busy: false })

const emit = defineEmits<{
  (e: "apply", payload: { expense: FirestoreExpense; updates: Partial<FirestoreExpense>; toastKey: string }): void
  (e: "skip", expense: FirestoreExpense): void
  (e: "finish"): void
}>()

const { t } = useI18n()
const { format: formatDateValue } = useFormatDate()
const formatDate = (date: string) => formatDateValue(date, "short")

// The action labels are read through $t in the template; the swipe hint needs
// the resolved string, so it goes through the same catalogue.
const tKey = (key: string): string => t(key)

const index = ref(0)
const handled = ref(0)
const pickedCategory = ref("")

const current = computed<ReviewCard | undefined>(() => props.cards[index.value])
const remaining = computed(() => Math.max(0, props.cards.length - index.value))

const categoryOptions = computed(() => props.categories.map((c) => ({ value: c.id, label: c.name, color: c.color })))

const accountLabel = computed(() => {
  const expense = current.value?.expense
  if (!expense) return ""
  if (expense.accountId) {
    const account = props.accounts.find((a) => a.id === expense.accountId)
    if (account) return account.name
  }
  return expense.account || ""
})

const suggestedCategory = computed(() => {
  const card = current.value
  if (!card) return null
  if (card.reason !== "uncategorized_bank" && card.reason !== "uncategorized_manual") return null
  const id = suggestCategoryForMerchant(props.suggestionIndex, card.expense.description)
  if (!id) return null
  return props.categories.find((c) => c.id === id) ?? null
})

const reasonVariant = computed(() => {
  const reason = current.value?.reason
  if (reason === "suspected_duplicate") return "warning"
  if (reason === "possible_transfer") return "info"
  return "neutral"
})

/**
 * What swipe-right means on this card. Null when there is no single obvious
 * accept — an uncategorized row with no history has nothing to guess, and
 * guessing wrong is worse than asking.
 */
const primary = computed<{ label: string; updates: Partial<FirestoreExpense>; toastKey: string } | null>(() => {
  const card = current.value
  if (!card) return null
  if (card.reason === "suspected_duplicate") {
    return {
      label: tKey("review.action.confirm_duplicate"),
      updates: { reviewed: true },
      toastKey: "review.toast.duplicate_kept",
    }
  }
  if (card.reason === "possible_transfer") {
    return {
      label: tKey("review.action.confirm_transfer"),
      updates: { isTransfer: true, reviewed: true },
      toastKey: "review.toast.transfer_marked",
    }
  }
  const suggestion = suggestedCategory.value
  if (!suggestion) return null
  return {
    label: suggestion.name,
    updates: { category: suggestion.id, reviewed: true },
    toastKey: "review.toast.categorized",
  }
})

// --- Applying an answer ------------------------------------------------------

function apply(updates: Partial<FirestoreExpense>, toastKey: string) {
  const card = current.value
  if (!card || props.busy) return
  emit("apply", { expense: card.expense, updates, toastKey })
  handled.value += 1
  advance()
}

function skip() {
  const card = current.value
  if (!card) return
  // Deliberately does NOT set `reviewed`: a skipped row is still undecided and
  // must come back next week. That is the difference between "not now" and
  // "leave it uncategorized", which is its own button.
  emit("skip", card.expense)
  advance()
}

function advance() {
  hapticLight()
  pickedCategory.value = ""
  translateX.value = 0
  index.value += 1
}

// Picking from the select applies immediately — a separate confirm step on a
// deck of 30 cards is 30 extra taps for nothing.
watch(pickedCategory, (value) => {
  if (!value) return
  apply({ category: value, reviewed: true }, "review.toast.categorized")
})

// --- Gesture -----------------------------------------------------------------
// Pointer events rather than touch: the app is a PWA on desktop too, and a
// deck that only responds to fingers would be unusable with a mouse.

const cardEl = ref<HTMLElement | null>(null)
const translateX = ref(0)
const isDragging = ref(false)
const SWIPE_THRESHOLD = 96
const AXIS_LOCK_THRESHOLD = 8

let pointerId: number | null = null
let startX = 0
let startY = 0
let axis: "none" | "horizontal" | "vertical" = "none"

const cardStyle = computed(() => ({
  transform: `translateX(${translateX.value}px) rotate(${translateX.value / 40}deg)`,
}))

const hintOpacity = computed(() => ({
  skip: translateX.value < 0 ? Math.min(1, -translateX.value / SWIPE_THRESHOLD) : 0,
  accept: translateX.value > 0 ? Math.min(1, translateX.value / SWIPE_THRESHOLD) : 0,
}))

function onPointerDown(event: PointerEvent) {
  if (props.busy) return
  pointerId = event.pointerId
  startX = event.clientX
  startY = event.clientY
  axis = "none"
  isDragging.value = false
}

function onPointerMove(event: PointerEvent) {
  if (pointerId !== event.pointerId) return
  const dx = event.clientX - startX
  const dy = event.clientY - startY

  if (axis === "none") {
    if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return
    axis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical"
    if (axis === "horizontal") {
      isDragging.value = true
      // Keep receiving moves even if the finger leaves the card's box.
      cardEl.value?.setPointerCapture(event.pointerId)
    }
  }
  if (axis !== "horizontal") return
  translateX.value = dx
}

// Also the pointercancel handler: the OS can take the gesture away mid-drag
// (Android's back gesture is the same horizontal swipe), and without this the
// card would stay frozen wherever the finger left it.
function onPointerEnd(event: PointerEvent) {
  if (pointerId !== event.pointerId) return
  const wasDragging = isDragging.value
  const dx = translateX.value
  pointerId = null
  axis = "none"
  isDragging.value = false

  if (!wasDragging) {
    translateX.value = 0
    return
  }
  if (dx <= -SWIPE_THRESHOLD) {
    hapticMedium()
    skip()
    return
  }
  if (dx >= SWIPE_THRESHOLD && primary.value) {
    hapticMedium()
    apply(primary.value.updates, primary.value.toastKey)
    return
  }
  translateX.value = 0
}

// --- Keyboard ----------------------------------------------------------------
// Arrow keys mirror the swipes. Without this the deck is touch-only, which
// would make the whole feature unusable on the web build.

function onKeydown(event: KeyboardEvent) {
  if (props.busy || !current.value) return
  const target = event.target as HTMLElement | null
  // Don't hijack arrows inside the category search box.
  if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return

  if (event.key === "ArrowLeft") {
    event.preventDefault()
    skip()
  } else if (event.key === "ArrowRight" && primary.value) {
    event.preventDefault()
    apply(primary.value.updates, primary.value.toastKey)
  }
}

onMounted(() => document.addEventListener("keydown", onKeydown))
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown))

defineExpose({ handled })
</script>

<style scoped>
.review-deck {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-progress {
  flex-shrink: 0;
}

.review-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg, 8px);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  touch-action: pan-y;
  user-select: none;
  will-change: transform;
  transition: transform 200ms ease-out;
}

.review-card--dragging {
  transition: none;
  cursor: grabbing;
}

.review-hint {
  position: absolute;
  top: 16px;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  pointer-events: none;
  transition: opacity 120ms linear;
}

.review-hint--skip {
  left: 16px;
  background: var(--bg-secondary);
  color: var(--text-tertiary);
}

.review-hint--accept {
  right: 16px;
  background: var(--forest-100, var(--bg-secondary));
  color: var(--forest-600);
}

.review-amount {
  font-family: var(--font-display, inherit);
  font-size: var(--text-3xl, 1.875rem);
  font-weight: 600;
  color: var(--text-primary);
  font-feature-settings: 'tnum';
  margin: 8px 0 0;
}

.review-description {
  font-size: var(--text-base);
  color: var(--text-primary);
  font-weight: 500;
  margin: 0;
  word-break: break-word;
}

.review-meta {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
  margin: 0;
}

.review-question {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 12px 0 0;
}

.review-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.review-categorize {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.review-secondary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-subtle);
}
</style>
